import type { DropboxFolderResponseDto } from '@/apis/backend/generated/models/dropboxFolderResponseDto';

import { useSnackbar } from 'notistack';
import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from '@tanstack/react-query';
import { useOrganization } from '@/context/organization-context';
import { useNavigate, createFileRoute } from '@tanstack/react-router';
import { IntegrationResponseDtoType } from '@/apis/backend/generated/models/integrationResponseDtoType';
import { IntegrationResponseDtoStatus } from '@/apis/backend/generated/models/integrationResponseDtoStatus';
import {
  dropboxOAuthControllerInstall,
  useDropboxOAuthControllerDisconnect,
  useIntegrationsControllerListIntegrations,
  useDropboxFoldersControllerListFolders,
  useDropboxFoldersControllerTriggerSync,
  useDropboxFoldersControllerSelectFolders,
  useDropboxFoldersControllerGetSyncStatus,
  useDropboxFoldersControllerGetSelectedFolders,
} from '@/apis/backend/generated/integrations/integrations';

import {
  Box,
  Card,
  Chip,
  Stack,
  Alert,
  Button,
  Skeleton,
  Checkbox,
  Collapse,
  Typography,
  IconButton,
  CardContent,
  CircularProgress,
} from '@mui/material';

import { paths } from 'src/routes/paths';
import { Iconify } from 'src/components/iconify';

export const Route = createFileRoute(
  '/_authed/dashboard/o/$orgId/settings/client/brand/$clientId/integrations/dropbox'
)({
  component: DropboxIntegrationPage,
});

function DropboxIntegrationPage() {
  const { t } = useTranslation('settings');
  const navigate = useNavigate();
  const { orgId, clientId } = Route.useParams();
  const { activeOrganization, activeClient } = useOrganization();
  const { enqueueSnackbar } = useSnackbar();
  const queryClient = useQueryClient();

  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [isConnecting, setIsConnecting] = useState(false);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const oauthError = params.get('error');
    const oauthSuccess = params.get('success');
    if (oauthError) {
      setError(oauthError);
    }
    if (oauthSuccess) {
      setSuccess(t('dropbox.connectSuccess', 'Dropbox connected successfully. Select folders to sync below.'));
    }
    if (oauthError || oauthSuccess) {
      const url = new URL(window.location.href);
      url.searchParams.delete('error');
      url.searchParams.delete('success');
      url.searchParams.delete('step');
      window.history.replaceState({}, '', url.toString());
      queryClient.invalidateQueries({
        predicate: (query) => {
          const key = query.queryKey[0];
          return typeof key === 'string' && key.includes('/integrations');
        },
      });
    }
  }, [queryClient, t]);

  const { data: integrationsData, isLoading: integrationsLoading } =
    useIntegrationsControllerListIntegrations(
      activeOrganization?.id || '',
      activeClient?.id || '',
      {
        query: {
          enabled: !!activeOrganization?.id && !!activeClient?.id,
          staleTime: 30000,
        },
      }
    );

  const dropboxIntegration = integrationsData?.integrations?.find(
    (i) => i.type === IntegrationResponseDtoType.dropbox
  );

  const isConnected =
    dropboxIntegration?.status === IntegrationResponseDtoStatus.active ||
    dropboxIntegration?.status === IntegrationResponseDtoStatus.syncing;

  const handleConnect = async () => {
    try {
      setIsConnecting(true);
      setError(null);

      if (!activeClient?.id || !activeOrganization?.id) {
        setError(t('dropbox.noClientOrOrg', 'No active client or organization selected.'));
        return;
      }

      const response = await dropboxOAuthControllerInstall({
        clientId: activeClient.id,
        orgId: activeOrganization.id,
        source: 'settings',
      });

      if (response?.redirectUrl) {
        window.location.href = response.redirectUrl;
      } else {
        throw new Error('No redirect URL received from server');
      }
    } catch (err) {
      console.error('Failed to connect Dropbox:', err);
      setError(t('dropbox.connectError', 'Failed to initiate Dropbox connection. Please try again.'));
      enqueueSnackbar(t('dropbox.connectErrorSnackbar', 'Failed to connect Dropbox'), { variant: 'error' });
    } finally {
      setIsConnecting(false);
    }
  };

  return (
    <Box>
      <Button
        size="small"
        startIcon={<Iconify icon="eva:arrow-back-fill" width={16} />}
        onClick={() =>
          navigate({
            to: paths.dashboard.orgSettings(orgId).client.clientIntegrations(clientId),
          })
        }
        sx={{ mb: 2, textTransform: 'none' }}
      >
        {t('dropbox.backToIntegrations', 'Back to Integrations')}
      </Button>

      <Card>
        <CardContent>
          {integrationsLoading ? (
            <Stack spacing={2}>
              <Skeleton variant="rounded" height={24} width="60%" />
              <Skeleton variant="rounded" height={18} width="40%" />
              <Skeleton variant="rounded" height={14} width="30%" />
            </Stack>
          ) : (
            <Stack spacing={3}>
              <IntegrationHeader isConnected={isConnected} />

              {error && (
                <Alert severity="error" onClose={() => setError(null)}>
                  {error}
                </Alert>
              )}
              {success && (
                <Alert severity="success" onClose={() => setSuccess(null)}>
                  {success}
                </Alert>
              )}

              {!isConnected ? (
                <NotConnectedState isConnecting={isConnecting} onConnect={handleConnect} />
              ) : (
                <ConnectedState />
              )}
            </Stack>
          )}
        </CardContent>
      </Card>
    </Box>
  );
}

function IntegrationHeader({ isConnected }: { isConnected: boolean }) {
  const { t } = useTranslation('settings');

  return (
    <Stack direction="row" spacing={2} alignItems="center">
      <Box component="img" src="/logos/dropbox.svg" alt="Dropbox" sx={{ width: 40, height: 40 }} />
      <Box sx={{ flex: 1 }}>
        <Typography variant="h6">Dropbox</Typography>
        <Typography variant="body2" color="text.secondary">
          {t('dropbox.headerSubtitle', 'Sync images from Dropbox folders to your media library')}
        </Typography>
      </Box>
      <Chip
        label={isConnected ? t('dropbox.statusConnected', 'Connected') : t('dropbox.statusNotConnected', 'Not Connected')}
        color={isConnected ? 'success' : 'default'}
        size="small"
        variant="outlined"
      />
    </Stack>
  );
}

function NotConnectedState({
  isConnecting,
  onConnect,
}: {
  isConnecting: boolean;
  onConnect: () => void;
}) {
  const { t } = useTranslation('settings');

  return (
    <Stack spacing={2} alignItems="flex-start">
      <Typography variant="body2" color="text.secondary">
        {t('dropbox.notConnectedDescription', 'Connect your Dropbox account to automatically sync images from selected folders into your media library. Images will be categorized based on folder names.')}
      </Typography>
      <Button
        variant="contained"
        onClick={onConnect}
        disabled={isConnecting}
        startIcon={
          isConnecting ? <CircularProgress size={20} color="inherit" /> : <Iconify icon="mdi:dropbox" width={20} />
        }
      >
        {isConnecting ? t('dropbox.connecting', 'Connecting...') : t('dropbox.connectButton', 'Connect Dropbox')}
      </Button>
    </Stack>
  );
}

function ConnectedState() {
  const { t } = useTranslation('settings');
  const { activeOrganization, activeClient } = useOrganization();
  const { enqueueSnackbar } = useSnackbar();
  const queryClient = useQueryClient();

  const orgId = activeOrganization?.id || '';
  const clientId = activeClient?.id || '';

  const { data: selectedData, isLoading: selectedLoading } =
    useDropboxFoldersControllerGetSelectedFolders(
      { clientId, orgId },
      { query: { enabled: !!orgId && !!clientId } }
    );

  const { data: syncStatus } = useDropboxFoldersControllerGetSyncStatus(
    { clientId, orgId },
    { query: { enabled: !!orgId && !!clientId, refetchInterval: 15000 } }
  );

  const disconnectMutation = useDropboxOAuthControllerDisconnect({
    mutation: {
      onSuccess: () => {
        enqueueSnackbar(t('dropbox.disconnectSuccess', 'Dropbox disconnected'), { variant: 'success' });
        queryClient.invalidateQueries({
          predicate: (query) => {
            const key = query.queryKey[0];
            return typeof key === 'string' && key.includes('/integrations');
          },
        });
      },
      onError: () => {
        enqueueSnackbar(t('dropbox.disconnectError', 'Failed to disconnect'), { variant: 'error' });
      },
    },
  });

  const syncMutation = useDropboxFoldersControllerTriggerSync({
    mutation: {
      onSuccess: (data) => {
        enqueueSnackbar(data.message || t('dropbox.syncStarted', 'Sync started'), { variant: 'success' });
        queryClient.invalidateQueries({
          predicate: (query) => {
            const key = query.queryKey[0];
            return typeof key === 'string' && key.includes('/dropbox');
          },
        });
      },
      onError: () => {
        enqueueSnackbar(t('dropbox.syncError', 'Failed to trigger sync'), { variant: 'error' });
      },
    },
  });

  const handleDisconnect = () => {
    if (!window.confirm(t('dropbox.disconnectConfirm', 'Disconnect Dropbox? Synced media will remain in your library.'))) return;
    disconnectMutation.mutate({ params: { clientId, orgId } });
  };

  const handleSync = () => {
    syncMutation.mutate({ params: { clientId, orgId } });
  };

  const selectedFolders = selectedData?.folders || [];
  const hasSelectedFolders = selectedFolders.length > 0;

  return (
    <Stack spacing={3}>
      {syncStatus && (
        <SyncStatusInfo
          status={syncStatus.status}
          lastSyncAt={syncStatus.lastSyncAt}
          lastError={syncStatus.lastError}
          folderCount={syncStatus.folderCount}
        />
      )}

      {selectedLoading ? (
        <Skeleton variant="rounded" height={60} />
      ) : hasSelectedFolders ? (
        <Box>
          <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
            <Typography variant="subtitle2">{t('dropbox.syncedFolders', 'Synced Folders')}</Typography>
            <Button
              size="small"
              onClick={handleSync}
              disabled={syncMutation.isPending}
              startIcon={
                syncMutation.isPending ? (
                  <CircularProgress size={16} color="inherit" />
                ) : (
                  <Iconify icon="eva:refresh-outline" width={16} />
                )
              }
            >
              {t('dropbox.syncNow', 'Sync Now')}
            </Button>
          </Stack>
          <Stack spacing={0.5}>
            {selectedFolders.map((f) => (
              <Stack
                key={f.folderPath}
                direction="row"
                alignItems="center"
                spacing={1}
                sx={{ py: 0.75, px: 1.5, bgcolor: 'action.hover', borderRadius: 1 }}
              >
                <Iconify icon="eva:folder-fill" width={18} sx={{ color: 'text.secondary' }} />
                <Typography variant="body2">{f.folderName}</Typography>
              </Stack>
            ))}
          </Stack>
        </Box>
      ) : (
        <Alert severity="info">
          {t('dropbox.noFoldersSelected', 'No folders selected yet. Choose folders below to start syncing images.')}
        </Alert>
      )}

      <FolderPicker selectedFolders={selectedFolders} />

      <Box sx={{ pt: 1, borderTop: 1, borderColor: 'divider' }}>
        <Button
          color="error"
          size="small"
          onClick={handleDisconnect}
          disabled={disconnectMutation.isPending}
          startIcon={<Iconify icon="eva:close-circle-outline" width={16} />}
        >
          {t('dropbox.disconnectButton', 'Disconnect Dropbox')}
        </Button>
      </Box>
    </Stack>
  );
}

function SyncStatusInfo({
  status,
  lastSyncAt,
  lastError,
  folderCount,
}: {
  status: string;
  lastSyncAt?: string | null;
  lastError?: string | null;
  folderCount: number;
}) {
  const { t } = useTranslation('settings');
  const isSyncing = status === 'syncing';

  return (
    <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap">
      <Chip
        label={isSyncing ? t('dropbox.statusSyncing', 'Syncing...') : t('dropbox.statusActive', 'Active')}
        color={isSyncing ? 'warning' : 'success'}
        size="small"
        icon={isSyncing ? <CircularProgress size={14} color="inherit" /> : undefined}
      />
      {folderCount > 0 && (
        <Typography variant="caption" color="text.secondary">
          {t('dropbox.folderCount', '{{count}} folder selected', { count: folderCount, defaultValue_other: '{{count}} folders selected' })}
        </Typography>
      )}
      {lastSyncAt && (
        <Typography variant="caption" color="text.secondary">
          {t('dropbox.lastSynced', 'Last synced: {{date}}', { date: new Date(lastSyncAt).toLocaleString() })}
        </Typography>
      )}
      {lastError && (
        <Typography variant="caption" color="error.main">
          {t('dropbox.syncErrorLabel', 'Error: {{message}}', { message: lastError })}
        </Typography>
      )}
    </Stack>
  );
}

function FolderPicker({
  selectedFolders,
}: {
  selectedFolders: Array<{ folderPath: string; folderName: string }>;
}) {
  const { t } = useTranslation('settings');
  const { activeOrganization, activeClient } = useOrganization();
  const { enqueueSnackbar } = useSnackbar();
  const queryClient = useQueryClient();

  const orgId = activeOrganization?.id || '';
  const clientId = activeClient?.id || '';

  const [isOpen, setIsOpen] = useState(false);
  const [checked, setChecked] = useState<Map<string, string>>(new Map());
  const [expandedFolders, setExpandedFolders] = useState<Set<string>>(new Set());

  useEffect(() => {
    const initial = new Map<string, string>();
    for (const f of selectedFolders) {
      initial.set(f.folderPath, f.folderName);
    }
    setChecked(initial);
  }, [selectedFolders]);

  const { data: rootFolders, isLoading: rootLoading } =
    useDropboxFoldersControllerListFolders(
      { clientId, orgId },
      { query: { enabled: !!orgId && !!clientId && isOpen } }
    );

  const selectMutation = useDropboxFoldersControllerSelectFolders({
    mutation: {
      onSuccess: (data) => {
        enqueueSnackbar(data.message || t('dropbox.foldersSaved', 'Folders saved'), { variant: 'success' });
        queryClient.invalidateQueries({
          predicate: (query) => {
            const key = query.queryKey[0];
            return typeof key === 'string' && key.includes('/dropbox');
          },
        });
        setIsOpen(false);
      },
      onError: () => {
        enqueueSnackbar(t('dropbox.saveFoldersError', 'Failed to save folder selection'), { variant: 'error' });
      },
    },
  });

  const toggleFolder = (path: string, name: string) => {
    setChecked((prev) => {
      const next = new Map(prev);
      if (next.has(path)) {
        next.delete(path);
      } else {
        next.set(path, name);
      }
      return next;
    });
  };

  const handleSave = () => {
    if (checked.size === 0) {
      enqueueSnackbar(t('dropbox.selectAtLeastOne', 'Select at least one folder'), { variant: 'warning' });
      return;
    }
    selectMutation.mutate({
      data: {
        folders: Array.from(checked.entries()).map(([folderPath, folderName]) => ({
          folderPath,
          folderName,
        })),
      },
      params: { clientId, orgId },
    });
  };

  const toggleExpand = (path: string) => {
    setExpandedFolders((prev) => {
      const next = new Set(prev);
      if (next.has(path)) {
        next.delete(path);
      } else {
        next.add(path);
      }
      return next;
    });
  };

  return (
    <Box>
      <Button
        size="small"
        variant="outlined"
        onClick={() => setIsOpen(!isOpen)}
        startIcon={<Iconify icon={isOpen ? 'eva:close-outline' : 'eva:folder-add-outline'} width={16} />}
      >
        {isOpen
          ? t('common:cancel')
          : selectedFolders.length > 0
            ? t('dropbox.changeFolders', 'Change Folders')
            : t('dropbox.selectFolders', 'Select Folders')}
      </Button>

      <Collapse in={isOpen}>
        <Box sx={{ mt: 2, border: 1, borderColor: 'divider', borderRadius: 1, p: 2 }}>
          <Typography variant="subtitle2" sx={{ mb: 1 }}>
            {t('dropbox.chooseFolders', 'Choose folders to sync')}
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ mb: 2, display: 'block' }}>
            {t('dropbox.chooseFoldersHint', 'Images from selected folders will be imported into your media library.')}
          </Typography>

          {rootLoading ? (
            <Stack spacing={1}>
              {[1, 2, 3].map((i) => (
                <Skeleton key={i} variant="rounded" height={36} />
              ))}
            </Stack>
          ) : !rootFolders?.folders?.length ? (
            <Typography variant="body2" color="text.secondary">
              {t('dropbox.noFoldersFound', 'No folders found in your Dropbox.')}
            </Typography>
          ) : (
            <Stack spacing={0}>
              {rootFolders.folders.map((folder) => (
                <FolderRow
                  key={folder.path}
                  folder={folder}
                  checked={checked}
                  onToggle={toggleFolder}
                  expandedFolders={expandedFolders}
                  onToggleExpand={toggleExpand}
                  depth={0}
                />
              ))}
            </Stack>
          )}

          <Stack direction="row" spacing={1} sx={{ mt: 2 }}>
            <Button
              variant="contained"
              size="small"
              onClick={handleSave}
              disabled={selectMutation.isPending || checked.size === 0}
              startIcon={selectMutation.isPending ? <CircularProgress size={16} color="inherit" /> : null}
            >
              {selectMutation.isPending
                ? t('dropbox.saving', 'Saving...')
                : t('dropbox.saveSelected', 'Save ({{count}} selected)', { count: checked.size })}
            </Button>
            <Button size="small" onClick={() => setIsOpen(false)}>
              {t('common:cancel')}
            </Button>
          </Stack>
        </Box>
      </Collapse>
    </Box>
  );
}

function FolderRow({
  folder,
  checked,
  onToggle,
  expandedFolders,
  onToggleExpand,
  depth,
}: {
  folder: DropboxFolderResponseDto;
  checked: Map<string, string>;
  onToggle: (path: string, name: string) => void;
  expandedFolders: Set<string>;
  onToggleExpand: (path: string) => void;
  depth: number;
}) {
  const { activeOrganization, activeClient } = useOrganization();
  const orgId = activeOrganization?.id || '';
  const clientId = activeClient?.id || '';

  const expanded = expandedFolders.has(folder.path);

  const { data: children, isLoading: childrenLoading } =
    useDropboxFoldersControllerListFolders(
      { clientId, orgId, parentPath: folder.path },
      { query: { enabled: !!orgId && !!clientId && expanded } }
    );

  return (
    <Box>
      <Stack
        direction="row"
        alignItems="center"
        sx={{ py: 0.5, pl: depth * 3, '&:hover': { bgcolor: 'action.hover' }, borderRadius: 0.5 }}
      >
        {folder.hasChildren ? (
          <IconButton size="small" onClick={() => onToggleExpand(folder.path)} sx={{ mr: 0.5 }}>
            <Iconify icon={expanded ? 'eva:chevron-down-fill' : 'eva:chevron-right-fill'} width={16} />
          </IconButton>
        ) : (
          <Box sx={{ width: 32, mr: 0.5 }} />
        )}
        <Checkbox
          size="small"
          checked={checked.has(folder.path)}
          onChange={() => onToggle(folder.path, folder.name)}
        />
        <Iconify icon="eva:folder-fill" width={18} sx={{ mr: 1, color: 'text.secondary' }} />
        <Typography variant="body2">{folder.name}</Typography>
      </Stack>

      <Collapse in={expanded}>
        {childrenLoading ? (
          <Box sx={{ pl: (depth + 1) * 3 + 4, py: 0.5 }}>
            <CircularProgress size={16} />
          </Box>
        ) : (
          children?.folders?.map((child) => (
            <FolderRow
              key={child.path}
              folder={child}
              checked={checked}
              onToggle={onToggle}
              expandedFolders={expandedFolders}
              onToggleExpand={onToggleExpand}
              depth={depth + 1}
            />
          ))
        )}
      </Collapse>
    </Box>
  );
}
